/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable max-lines-per-function */
import {
  Box,
  Button,
  Flex,
  Heading,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useDisclosure
} from "@chakra-ui/react"
import { useState } from "react"
import bg from "../../assets/images/faqBanner.jpg"
import { breadCrumbFirstLevel, breadCrumbReportsAndResourcesSecondLevel, pageContentWidth } from "../../common/commonConstants"
import { buildDocumentUrl } from "../../common/documentUrl"
import { SubPage, SubPageContent } from "../../components/SubPage"
import { BreadCrumb } from "../../components/commons/breadcrumb"
import { StaticPageHeader } from "../../components/commons/static-page-header"
import { Colors } from "../../theme/colors"
import { useGetReportLazyQuery } from "../../type-and-hooks"

export const FAQ = (): JSX.Element => {
  const { isOpen, onOpen, onClose } = useDisclosure()
  const [loadingReport, setLoadingReport] = useState<string | null>(null)
  const [getReport] = useGetReportLazyQuery()

  const navigate = (id: string): void => {
    const element = document.getElementById(id)
    if (element) {
      const header = document.getElementById("main-navigation")
      const headerHeight = header ? header.getBoundingClientRect().height : 80
      const elementTop = element.getBoundingClientRect().top + window.scrollY
      window.scrollTo({ top: elementTop - headerHeight - 16, behavior: "smooth" })
    }
  }

  const handleKeyDown = (event: React.KeyboardEvent, id: string): void => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault()
      navigate(id)
    }
  }

  const handleReportClick = async (reportNo: string): Promise<void> => {
    setLoadingReport(reportNo)
    try {
      const { data } = await getReport({ variables: { reportNo } })
      const url = data?.getReport?.reportURL ?? null
      if (url) {
        window.open(buildDocumentUrl(data?.getReport?.fileName, url) ?? url, "_blank", "noopener,noreferrer")
        return
      }
      onOpen()
    } catch {
      onOpen()
    } finally {
      setLoadingReport(null)
    }
  }

  const ReportLink = ({ reportNo, label }: { reportNo: string; label?: string }): JSX.Element => (
    <Text
      as="span"
      color={Colors.Primary.accent}
      textDecoration="underline"
      cursor={loadingReport === reportNo ? "wait" : "pointer"}
      onClick={() => { void handleReportClick(reportNo) }}
    >
      {loadingReport === reportNo ? "Loading..." : (label ?? reportNo)}
    </Text>
  )

  return (
    <>
      <SubPage>
        <StaticPageHeader bg={bg}>
          <Flex
            maxW={["320px", "320px", "455px"]}
            align={"center"}
            flexDir={"column"}
            textAlign={"center"}
            px="20px"
            pt={["24px", "24px", "0px"]}
          >
            <Heading color={"primary"} variant={"Heading1SemiBold"} fontSize="40px">
              Frequently Asked Questions
            </Heading>
            <Heading mb={["0", "0", "24px"]} color={"primary"} variant={"Heading1SemiBold"} fontSize="40px">
              (FAQs)
            </Heading>
          </Flex>
        </StaticPageHeader>
        <BreadCrumb
          options={{
            ...breadCrumbFirstLevel,
            ...breadCrumbReportsAndResourcesSecondLevel,
            thirdLevel: { title: "FAQs" }
          }}
        />
        <SubPageContent innerBoxProps={{ w: ["100%", "100%", "100%", pageContentWidth, "60%", "60%"] }}>
          <Flex align={"center"} mt={["26px", "26px", "38px"]} gap="24px" flexDir={"column"} fontFamily="PT Sans">
            <Text variant={["RBody", "RBody", "Body1Regular"]} textAlign={["center"]} margin="auto">
              Click one of the questions listed here or scroll down to browse our FAQs:
            </Text>
            <Box w={"100%"} mt="34px">
              {/* FAQ Navigation */}
              <nav aria-label="FAQ navigation">
                <Box role="list" aria-label="Frequently asked questions">
                  <Button
                    variant={"linkButton"}
                    display="block"
                    fontWeight="normal"
                    fontSize={["16px", "16px", "22.5px"]}
                    lineHeight={["24px", "24px", "26px"]}
                    whiteSpace="break-spaces"
                    textAlign={"left"}
                    textTransform={["capitalize", "capitalize", "none"]}
                    onClick={() => { navigate("frl_question") }}
                    onKeyDown={e => { handleKeyDown(e, "frl_question") }}
                    mb={["8px", "8px", "18px"]}
                    role="listitem"
                    aria-label="Go to answer for: What is FRL?"
                    _focus={{ outline: "2px solid", outlineColor: Colors.Primary.accent, outlineOffset: "2px" }}
                  >
                    What is FRL?
                  </Button>

                  <Button
                    variant={"linkButton"}
                    display="block"
                    fontWeight="normal"
                    fontSize={["16px", "16px", "22.5px"]}
                    lineHeight={["24px", "24px", "26px"]}
                    whiteSpace="break-spaces"
                    textAlign={"left"}
                    textTransform={["capitalize", "capitalize", "none"]}
                    onClick={() => { navigate("gap_sizes_question") }}
                    onKeyDown={e => { handleKeyDown(e, "gap_sizes_question") }}
                    mb={["8px", "8px", "18px"]}
                    role="listitem"
                    aria-label="Go to answer for: What are the maximum annular gap sizes between the element and the pipe and how do I treat them?"
                    _focus={{ outline: "2px solid", outlineColor: Colors.Primary.accent, outlineOffset: "2px" }}
                  >
                    What are the maximum annular gap sizes between the element and the pipe and how do I treat them?
                  </Button>

                  <Button
                    variant={"linkButton"}
                    display="block"
                    fontWeight="normal"
                    fontSize={["16px", "16px", "22.5px"]}
                    lineHeight={["24px", "24px", "26px"]}
                    whiteSpace="break-spaces"
                    textAlign={"left"}
                    textTransform={["capitalize", "capitalize", "none"]}
                    onClick={() => { navigate("spacing_requirements_question") }}
                    onKeyDown={e => { handleKeyDown(e, "spacing_requirements_question") }}
                    mb={["8px", "8px", "18px"]}
                    role="listitem"
                    aria-label="Go to answer for: Is there a minimum spacing requirement between SNAP collars?"
                    _focus={{ outline: "2px solid", outlineColor: Colors.Primary.accent, outlineOffset: "2px" }}
                  >
                    Is there a minimum spacing requirement between SNAP collars?
                  </Button>

                  <Button
                    variant={"linkButton"}
                    display="block"
                    fontWeight="normal"
                    fontSize={["16px", "16px", "22.5px"]}
                    lineHeight={["24px", "24px", "26px"]}
                    whiteSpace="break-spaces"
                    textAlign={"left"}
                    textTransform={["capitalize", "capitalize", "none"]}
                    onClick={() => { navigate("acoustic_lagging_question") }}
                    onKeyDown={e => { handleKeyDown(e, "acoustic_lagging_question") }}
                    mb={["8px", "8px", "18px"]}
                    role="listitem"
                    aria-label="Go to answer for: Is it okay to leave acoustic pipe lagging under the collar?"
                    _focus={{ outline: "2px solid", outlineColor: Colors.Primary.accent, outlineOffset: "2px" }}
                  >
                    Is it okay to leave acoustic pipe lagging under the collar?
                  </Button>

                  <Button
                    variant={"linkButton"}
                    display="block"
                    fontWeight="normal"
                    fontSize={["16px", "16px", "22.5px"]}
                    lineHeight={["24px", "24px", "26px"]}
                    whiteSpace="break-spaces"
                    textAlign={"left"}
                    textTransform={["capitalize", "capitalize", "none"]}
                    onClick={() => { navigate("metal_formwork_question") }}
                    onKeyDown={e => { handleKeyDown(e, "metal_formwork_question") }}
                    mb={["8px", "8px", "18px"]}
                    role="listitem"
                    aria-label="Go to answer for: How can I secure Snap Fire Collars to metal formwork?"
                    _focus={{ outline: "2px solid", outlineColor: Colors.Primary.accent, outlineOffset: "2px" }}
                  >
                    How can I secure Snap Fire Collars to metal formwork?
                  </Button>

                  <Button
                    variant={"linkButton"}
                    display="block"
                    fontWeight="normal"
                    fontSize={["16px", "16px", "22.5px"]}
                    lineHeight={["24px", "24px", "26px"]}
                    whiteSpace="break-spaces"
                    textAlign={"left"}
                    textTransform={["capitalize", "capitalize", "none"]}
                    onClick={() => { navigate("fuselink_replacement_question") }}
                    onKeyDown={e => { handleKeyDown(e, "fuselink_replacement_question") }}
                    mb={["8px", "8px", "18px"]}
                    role="listitem"
                    aria-label="Go to answer for: If a fuselink breaks or is activated once the pipe is installed, does the collar need to be replaced?"
                    _focus={{ outline: "2px solid", outlineColor: Colors.Primary.accent, outlineOffset: "2px" }}
                  >
                    If a fuselink breaks or is activated once the pipe is installed, does the collar need to be replaced?
                  </Button>

                  <Button
                    variant={"linkButton"}
                    display="block"
                    fontWeight="normal"
                    fontSize={["16px", "16px", "22.5px"]}
                    lineHeight={["24px", "24px", "26px"]}
                    whiteSpace="break-spaces"
                    textAlign={"left"}
                    textTransform={["capitalize", "capitalize", "none"]}
                    onClick={() => { navigate("collar_alteration_question") }}
                    onKeyDown={e => { handleKeyDown(e, "collar_alteration_question") }}
                    mb={["8px", "8px", "18px"]}
                    role="listitem"
                    aria-label="Go to answer for: Can the collar be altered in any way?"
                    _focus={{ outline: "2px solid", outlineColor: Colors.Primary.accent, outlineOffset: "2px" }}
                  >
                    Can the collar be altered in any way?
                  </Button>

                  <Button
                    variant={"linkButton"}
                    display="block"
                    fontWeight="normal"
                    fontSize={["16px", "16px", "22.5px"]}
                    lineHeight={["24px", "24px", "26px"]}
                    whiteSpace="break-spaces"
                    textAlign={"left"}
                    textTransform={["capitalize", "capitalize", "none"]}
                    onClick={() => { navigate("concrete_spring_pockets_question") }}
                    onKeyDown={e => { handleKeyDown(e, "concrete_spring_pockets_question") }}
                    mb={["8px", "8px", "18px"]}
                    role="listitem"
                    aria-label="Go to answer for: Is concrete allowed in the spring pockets?"
                    _focus={{ outline: "2px solid", outlineColor: Colors.Primary.accent, outlineOffset: "2px" }}
                  >
                    Is concrete allowed in the spring pockets?
                  </Button>

                  <Button
                    variant={"linkButton"}
                    display="block"
                    fontWeight="normal"
                    fontSize={["16px", "16px", "22.5px"]}
                    lineHeight={["24px", "24px", "26px"]}
                    whiteSpace="break-spaces"
                    textAlign={"left"}
                    textTransform={["capitalize", "capitalize", "none"]}
                    onClick={() => { navigate("fasteners_question") }}
                    onKeyDown={e => { handleKeyDown(e, "fasteners_question") }}
                    mb={["8px", "8px", "18px"]}
                    role="listitem"
                    aria-label="Go to answer for: What fasteners can I use to fix Snap retrofit fire collars to an element?"
                    _focus={{ outline: "2px solid", outlineColor: Colors.Primary.accent, outlineOffset: "2px" }}
                  >
                    What fasteners can I use to fix Snap retrofit fire collars to an element?
                  </Button>

                  <Button
                    variant={"linkButton"}
                    display="block"
                    fontWeight="normal"
                    fontSize={["16px", "16px", "22.5px"]}
                    lineHeight={["24px", "24px", "26px"]}
                    whiteSpace="break-spaces"
                    textAlign={"left"}
                    textTransform={["capitalize", "capitalize", "none"]}
                    onClick={() => { navigate("floor_waste_solution_question") }}
                    onKeyDown={e => { handleKeyDown(e, "floor_waste_solution_question") }}
                    mb={["8px", "8px", "18px"]}
                    role="listitem"
                    aria-label="Go to answer for: Do you have a solution for where a stack collar is installed in a floor waste configuration?"
                    _focus={{ outline: "2px solid", outlineColor: Colors.Primary.accent, outlineOffset: "2px" }}
                  >
                    Do you have a solution for where a stack collar is installed in a floor waste configuration?
                  </Button>
                </Box>
              </nav>

              {/* FAQ Content */}
              <Box
                flexWrap={"wrap"}
                alignItems={"flex-start"}
                mt={["32px", "32px", "74px"]}
                justifyContent={"flex-start"}
                mb="0"
                role="main"
                aria-label="FAQ answers"
              >
                {/* FRL */}
                <Box id="frl_question" mt="8" className="MDX-component" role="article" aria-labelledby="faq-question-frl">
                  <Heading id="faq-question-frl" display="block" variant={["RHeading4", "RHeading4", "Heading3PT"]} color={Colors.Secondary.c2} as="h3">
                    What is FRL?
                  </Heading>
                  <Box mt={["8px", "8px", "16px"]} color={Colors.Secondary.c2} lineHeight="21px">
                    <Text mb="16px" variant={"Body1Regular"}>
                      FRL stands for Fire Resistance Level. It&apos;s the measure of how long a building element such as a wall, floor or fire collar
                      can resist fire during testing in accordance with AS 1530.4, the Australian Standard for fire-resistance tests.
                    </Text>
                    <Text mb="16px" variant={"Body1Regular"}>
                      An FRL is expressed as three numbers, in minutes, which represent the following performance criteria:
                    </Text>
                    <Box mt="16px" mb="16px" border="1px solid" borderColor="gray.200" borderRadius="md" overflow="hidden">
                      <Box display="grid" gridTemplateColumns="repeat(3, 1fr)" gap="0">
                        <Box bg={Colors.Primary.accent} color="white" fontWeight="700" px="4" py="3" fontSize="14px" textAlign="center" display="flex" alignItems="center" justifyContent="center" fontFamily="PT Sans">Component</Box>
                        <Box bg={Colors.Primary.accent} color="white" fontWeight="700" px="4" py="3" fontSize="14px" textAlign="center" display="flex" alignItems="center" justifyContent="center" fontFamily="PT Sans">Details</Box>
                        <Box bg={Colors.Primary.accent} color="white" fontWeight="700" px="4" py="3" fontSize="14px" textAlign="center" display="flex" alignItems="center" justifyContent="center" fontFamily="PT Sans">Example (min)</Box>
                        <Box bg="white" px="4" py="3" fontSize="14px" borderTop="1px solid" borderRight="1px solid" borderColor="gray.200" textAlign="left" fontFamily="PT Sans">Structural adequacy</Box>
                        <Box bg="white" px="4" py="3" fontSize="14px" borderTop="1px solid" borderRight="1px solid" borderColor="gray.200" textAlign="left" fontFamily="PT Sans">The time the element can remain structurally sound and support its load</Box>
                        <Box bg="white" px="4" py="3" fontSize="14px" borderTop="1px solid" borderColor="gray.200" textAlign="center" display="flex" alignItems="center" justifyContent="center" fontFamily="PT Sans">120</Box>
                        <Box bg="white" px="4" py="3" fontSize="14px" borderTop="1px solid" borderRight="1px solid" borderColor="gray.200" textAlign="left" fontFamily="PT Sans">Integrity</Box>
                        <Box bg="white" px="4" py="3" fontSize="14px" borderTop="1px solid" borderRight="1px solid" borderColor="gray.200" textAlign="left" fontFamily="PT Sans">The time it can prevent flames or hot gases from passing through</Box>
                        <Box bg="white" px="4" py="3" fontSize="14px" borderTop="1px solid" borderColor="gray.200" textAlign="center" display="flex" alignItems="center" justifyContent="center" fontFamily="PT Sans">120</Box>
                        <Box bg="white" px="4" py="3" fontSize="14px" borderTop="1px solid" borderRight="1px solid" borderColor="gray.200" textAlign="left" fontFamily="PT Sans">Insulation</Box>
                        <Box bg="white" px="4" py="3" fontSize="14px" borderTop="1px solid" borderRight="1px solid" borderColor="gray.200" textAlign="left" fontFamily="PT Sans">The time it can stop heat from transferring through to the unexposed side</Box>
                        <Box bg="white" px="4" py="3" fontSize="14px" borderTop="1px solid" borderColor="gray.200" textAlign="center" display="flex" alignItems="center" justifyContent="center" fontFamily="PT Sans">120</Box>
                      </Box>
                    </Box>
                    <Text mb="16px" variant={"Body1Regular"}>
                      For example, an FRL of 120/120/120 indicates the element maintained all three performance measures for at least 120 minutes under
                      test conditions.
                    </Text>
                    <Text variant={"Body1Regular"}>
                      As fire collars are non-loadbearing components, only the{" "}
                      <Text as="span" fontWeight="bold" color={Colors.Primary.c1}>integrity</Text>{" "}
                      and{" "}
                      <Text as="span" fontWeight="bold" color={Colors.Primary.c1}>insulation</Text>{" "}
                      criteria are applicable. Therefore, all our FRLs are expressed in the format –/xxx/xxx. For example –/120/120.
                    </Text>
                  </Box>
                </Box>

                {/* Gap Sizes */}
                <Box id="gap_sizes_question" mt="8" className="MDX-component" role="article" aria-labelledby="faq-question-gap-sizes">
                  <Heading id="faq-question-gap-sizes" display="block" variant={["RHeading4", "RHeading4", "Heading3PT"]} color={Colors.Secondary.c2} as="h3">
                    What are the maximum annular gap sizes between the element and the pipe and how do I treat them?
                  </Heading>
                  <Box mt={["8px", "8px", "16px"]} color={Colors.Secondary.c2} lineHeight="21px">
                    <Text mb="16px" variant={"Body1Regular"}>
                      The allowable annular gap and treatment will vary depending on the collar, element and service penetrating. For the following
                      collars, a standard annular gap treatment table is available, which specifies the allowable gap and required treatment method:
                    </Text>
                    <Box as="ul" mb="16px" pl="20px">
                      <Box as="li" mb="8px">
                        Plastic body cast-in collars up to and including the H150, see{" "}
                        <ReportLink reportNo="FCO3426" />{" "}
                        Table 17 and Section 3.2 (c)<sup>¹</sup>
                      </Box>
                      <Box as="li" mb="8px">
                        Utility Collar, see{" "}
                        <ReportLink reportNo="FCO3520" />{" "}
                        Table 3
                      </Box>
                      <Box as="li" mb="8px">
                        MS70C, see{" "}
                        <ReportLink reportNo="FCO3481" />{" "}
                        Table 1
                      </Box>
                      <Box as="li" mb="8px">
                        MS70R, see{" "}
                        <ReportLink reportNo="FCO3397" />{" "}
                        Tables 4, 5, 9 &amp; 10
                      </Box>
                    </Box>
                    <Box mt="16px" pt="16px" borderTop="1px solid" borderColor="gray.300">
                      <Text variant={"Body1Regular"} fontSize="14px" color="gray.600">
                        <sup>¹</sup>For the GAS50C refer to the reports associated with this product not the information supplied above
                      </Text>
                    </Box>
                    <Text mt="16px" mb="16px" variant={"Body1Regular"}>
                      For all other products, the allowable annular gap size and gap treatment must be determined in accordance with the relevant test
                      or assessment report.
                    </Text>
                  </Box>
                </Box>

                {/* Spacing Requirements */}
                <Box id="spacing_requirements_question" mt="8" className="MDX-component" role="article" aria-labelledby="faq-question-spacing-requirements">
                  <Heading id="faq-question-spacing-requirements" display="block" variant={["RHeading4", "RHeading4", "Heading3PT"]} color={Colors.Secondary.c2} as="h3">
                    Is there a minimum spacing requirement between SNAP collars?
                  </Heading>
                  <Box mt={["8px", "8px", "16px"]} color={Colors.Secondary.c2} lineHeight="21px">
                    <Text mb="12px" variant={"Body1Regular"} fontWeight="bold">Plastic body cast-in collars:</Text>
                    <Text mb="16px" variant={"Body1Regular"} pl="16px">
                      Snap stack and floor waste plastic body cast-in collars are approved for a minimum spacing of 40mm. The 40mm is not measured from
                      the flange but from spring pocket to spring pocket, collar body to collar body or collar body to spring pocket. Refer{" "}
                      <ReportLink reportNo="FCO3426" />{" "}
                      and{" "}
                      <ReportLink reportNo="FCO3426" label="SNAPShot #001" />.
                    </Text>
                    <Text mb="16px" variant={"Body1Regular"} pl="16px">
                      Additionally, plastic-body cast-in collars can be clustered in groups of up to 2 rows of 10, see{" "}
                      <ReportLink reportNo="FCO3426" />{" "}
                      Section 3.3(b)(i) and Fig 25a for details.
                    </Text>
                    <Text mb="12px" variant={"Body1Regular"} fontWeight="bold">Multiservice collars:</Text>
                    <Text mb="8px" variant={"Body1Regular"} pl="16px">The MS70R and MS70C have the following spacing requirements:</Text>
                    <Box as="ul" mb="8px" pl="36px">
                      <Box as="li" mb="8px">
                        <Text variant={"Body1Regular"}>
                          Minimum{" "}
                          <Text as="span" fontWeight="bold" color={Colors.Primary.c1}>80mm spacing</Text>{" "}
                          between spring pocket to spring pocket, collar body to collar body or collar body to spring pocket, without impacting the FRL
                        </Text>
                      </Box>
                      <Box as="li" mb="8px">
                        <Text variant={"Body1Regular"}>
                          Minimum{" "}
                          <Text as="span" fontWeight="bold" color={Colors.Primary.c1}>40mm spacing</Text>{" "}
                          between spring pocket to spring pocket, collar body to collar body or collar body to spring pocket for the reduced FRL outlined in:
                        </Text>
                      </Box>
                    </Box>
                    <Box as="ul" mb="16px" pl="64px">
                      <Box as="li" mb="8px">
                        <Text variant={"Body1Regular"}>MS70C, see <ReportLink reportNo="FCO3481" /> Table 7</Text>
                      </Box>
                      <Box as="li" mb="8px">
                        <Text variant={"Body1Regular"}>MS70R, see <ReportLink reportNo="FCO3397" /> Table 14 (Note: 40mm spacing only applies to wall applications)</Text>
                      </Box>
                    </Box>
                    <Text mb="12px" variant={"Body1Regular"} fontWeight="bold">Other Snap collars:</Text>
                    <Text mb="16px" variant={"Body1Regular"} pl="16px">
                      Our remaining range of collars do not have a defined minimum spacing requirement. However, the industry relies on AS4072.1-2005
                      clause 4.9.3 that specific{" "}
                      <Text as="span" fontStyle="italic">
                        the minimum distance between penetrations in a modular system shall not be less than 40mm, unless otherwise tested in specimen form.
                      </Text>{" "}
                      Where you have the ability to increase this spacing, we recommend doing so.
                    </Text>
                  </Box>
                </Box>

                {/* Acoustic Lagging */}
                <Box id="acoustic_lagging_question" mt="8" className="MDX-component" role="article" aria-labelledby="faq-question-acoustic-lagging">
                  <Heading id="faq-question-acoustic-lagging" display="block" variant={["RHeading4", "RHeading4", "Heading3PT"]} color={Colors.Secondary.c2} as="h3">
                    Is it okay to leave acoustic pipe lagging under the collar?
                  </Heading>
                  <Box mt={["8px", "8px", "16px"]} color={Colors.Secondary.c2} lineHeight="21px">
                    <Text mb="16px" variant={"Body1Regular"}>
                      Some of SNAP plastic body cast-in stack and floor waste collars are approved with lagging hard up against the base of the collar
                      provided you are using NuWrap 5 or Soundlag 4525C. Refer to <ReportLink reportNo="FCO3426" />, Table 17a to verify that your system is covered.
                    </Text>
                    <Text variant={"Body1Regular"}>
                      For all other collars including retrofit and large cast-in collars or other acoustic lagging brands, the lagging is required to be
                      cut away to sufficiently expose the collar.
                    </Text>
                  </Box>
                </Box>

                {/* Metal Formwork */}
                <Box id="metal_formwork_question" mt="8" className="MDX-component" role="article" aria-labelledby="faq-question-metal-formwork">
                  <Heading id="faq-question-metal-formwork" display="block" variant={["RHeading4", "RHeading4", "Heading3PT"]} color={Colors.Secondary.c2} as="h3">
                    How can I secure Snap Fire Collars to metal formwork?
                  </Heading>
                  <Box mt={["8px", "8px", "16px"]} color={Colors.Secondary.c2} lineHeight="21px">
                    <Text mb="16px" variant={"Body1Regular"}>
                      Check that the style of metal deck is supported by the necessary test or assessment report (reports describing conventional
                      concrete slabs are not sufficient evidence for compliance for metal deck systems).
                    </Text>
                    <Text mb="12px" variant={"Body1Regular"} fontWeight="bold">Utility Collar:</Text>
                    <Box as="ul" mb="16px" pl="20px">
                      <Box as="li" mb="8px">
                        <Text variant={"Body1Regular"}>Refer to <ReportLink reportNo="FCO3520" /> Table 4 &amp; 5 and Figure 4</Text>
                      </Box>
                    </Box>
                    <Text mb="12px" variant={"Body1Regular"} fontWeight="bold">Retrofit Collars:</Text>
                    <Box as="ul" mb="16px" pl="20px">
                      <Box as="li" mb="8px">
                        <Text variant={"Body1Regular"}>Refer to <ReportLink reportNo="FSP1953" /></Text>
                      </Box>
                    </Box>
                    <Text mb="12px" variant={"Body1Regular"} fontWeight="bold">Cast-in collars:</Text>
                    <Text mb="8px" variant={"Body1Regular"} pl="16px">Snap provides two options for fixing plastic body cast-in collars to metal formwork:</Text>
                    <Box as="ul" mb="16px" pl="36px">
                      <Box as="li" mb="8px">
                        <Text variant={"Body1Regular"}>
                          <Text as="span" fontWeight="bold" onClick={() => navigate("/products/accessory/Metal_Deck_Plates")} color={Colors.Primary.c1}>Metal Deck Plates</Text>{" "}
                          – Available in sizes to suit collars from H/L40 up to H150. For detailed instructions and hole size limitations, refer to the{" "}
                          <Text as="span" fontStyle="italic">Metal Deck Plate Installation Instructions</Text>.
                        </Text>
                      </Box>
                      <Box as="li" mb="8px">
                        <Text variant={"Body1Regular"}>
                          <Text as="span" fontWeight="bold" color={Colors.Primary.c1}>Steel Templates for Plasma Cutting</Text>{" "}
                          – Also available in sizes to suit collars from H/L50 up to H150. The templates are for use with a plasma cutter to create{" "}
                          <Text as="span" textDecoration="underline">the required cut-out shape to expose the active area of the collar in the metal deck</Text>.
                        </Text>
                      </Box>
                    </Box>
                  </Box>
                </Box>

                {/* Fuselink Replacement */}
                <Box id="fuselink_replacement_question" mt="8" className="MDX-component" role="article" aria-labelledby="faq-question-fuselink-replacement">
                  <Heading id="faq-question-fuselink-replacement" display="block" variant={["RHeading4", "RHeading4", "Heading3PT"]} color={Colors.Secondary.c2} as="h3">
                    If a fuselink breaks or is activated once the pipe is installed, does the collar need to be replaced?
                  </Heading>
                  <Box mt={["8px", "8px", "16px"]} color={Colors.Secondary.c2} lineHeight="21px">
                    <Text mb="16px" variant={"Body1Regular"}>
                      If the fuselink (the plastic link holding the spring ends together) breaks after the pipe has been installed, the fire collar is
                      still compliant as per <ReportLink reportNo="FCO3426" /> Section 3.2 (e)(v), and no further action is required.
                    </Text>
                    <Text variant={"Body1Regular"}>
                      However, if a fuselink is broken or missing upon receiving the product, please{" "}
                      <Text as="span" textDecoration="underline">
                        <a href="/contact-us">contact us</a>
                      </Text>.
                    </Text>
                  </Box>
                </Box>

                {/* Collar Alteration */}
                <Box id="collar_alteration_question" mt="8" className="MDX-component" role="article" aria-labelledby="faq-question-collar-alteration">
                  <Heading id="faq-question-collar-alteration" display="block" variant={["RHeading4", "RHeading4", "Heading3PT"]} color={Colors.Secondary.c2} as="h3">
                    Can the collar be altered in any way?
                  </Heading>
                  <Box mt={["8px", "8px", "16px"]} color={Colors.Secondary.c2} lineHeight="21px">
                    <Text mb="16px" variant={"Body1Regular"}>
                      Plastic-body cast-in collars may only be modified in the following circumstances as outlined in Section 3.2 of <ReportLink reportNo="FCO3426" />.
                    </Text>
                    <Box as="ul" mb="16px" pl="20px">
                      <Box as="li" mb="8px">
                        <Text variant={"Body1Regular"}>The top of the collar can be cut flush with the top of the slab</Text>
                      </Box>
                      <Box as="li" mb="8px">
                        <Text variant={"Body1Regular"}>The flange can be trimmed see <ReportLink reportNo="FCO3426" /> Figure 11</Text>
                      </Box>
                      <Box as="li" mb="8px">
                        <Text variant={"Body1Regular"}>
                          The collar can be cut above the active area to allow the pipe to roll over within the slab, see <ReportLink reportNo="FCO3426" /> Figure 12
                        </Text>
                      </Box>
                    </Box>
                  </Box>
                </Box>

                {/* Concrete Spring Pockets */}
                <Box id="concrete_spring_pockets_question" mt="8" className="MDX-component" role="article" aria-labelledby="faq-question-concrete-spring-pockets">
                  <Heading id="faq-question-concrete-spring-pockets" display="block" variant={["RHeading4", "RHeading4", "Heading3PT"]} color={Colors.Secondary.c2} as="h3">
                    Is concrete allowed in the spring pockets?
                  </Heading>
                  <Box mt={["8px", "8px", "16px"]} color={Colors.Secondary.c2} lineHeight="21px">
                    <Text mb="16px" variant={"Body1Regular"}>
                      Cured concrete cannot remain within the spring pockets. Any concrete that has leaked in during installation may prevent the collar
                      from activating in the event of a fire, leaving the collar non-compliant. Any concrete present in the spring pockets must be removed.
                    </Text>
                    <Text variant={"Body1Regular"}>
                      Concrete slurry can be located under the flange of the collar as per <ReportLink reportNo="FCO3426" /> Section 3.2 (e)(iv).
                    </Text>
                  </Box>
                </Box>

                {/* Fasteners */}
                <Box id="fasteners_question" mt="8" className="MDX-component" role="article" aria-labelledby="faq-question-fasteners">
                  <Heading id="faq-question-fasteners" display="block" variant={["RHeading4", "RHeading4", "Heading3PT"]} color={Colors.Secondary.c2} as="h3">
                    What fasteners can I use to fix Snap retrofit fire collars to an element?
                  </Heading>
                  <Box mt={["8px", "8px", "16px"]} color={Colors.Secondary.c2} lineHeight="21px">
                    <Text variant={"Body1Regular"}>
                      Refer to our{" "}
                      <Text as="span" variant={"Body1Regular"} color={Colors.Primary.accent} textDecoration="underline" cursor="pointer">
                        <a target="_blank" href="/instructions">Recommended Fixings Guide</a>
                      </Text>{" "}
                      for the correct fasteners for each approved element type.
                    </Text>
                  </Box>
                </Box>

                {/* Floor Waste Solution */}
                <Box id="floor_waste_solution_question" mt="8" className="MDX-component" role="article" aria-labelledby="faq-question-floor-waste-solution">
                  <Heading id="faq-question-floor-waste-solution" display="block" variant={["RHeading4", "RHeading4", "Heading3PT"]} color={Colors.Secondary.c2} as="h3">
                    Do you have a solution for where a stack collar is installed in a floor waste configuration?
                  </Heading>
                  <Box mt={["8px", "8px", "16px"]} color={Colors.Secondary.c2} lineHeight="21px">
                    <Text variant={"Body1Regular"}>
                      Where a Snap H100S or H50S cast-in collar has been incorrectly installed in a floor waste configuration, an LP100R or LP50R
                      Low-Profile retrofit collar respectively can be installed underneath the cast-in collar to ensure compliance. Refer to{" "}
                      <ReportLink reportNo="FSP1673" /> for the tested system.
                    </Text>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Flex>
        </SubPageContent>
      </SubPage>

      <Modal isOpen={isOpen} onClose={onClose} size="5xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent h="90vh">
          <ModalHeader color={Colors.Primary.c1} borderBottom="1px solid" borderColor={Colors.Secondary.c2_300} pr="48px">
            Report Unavailable
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody p="0">
            <Flex align="center" justify="center" h="100%" minH="70vh" flexDir="column" gap="12px" px="32px" textAlign="center">
              <Text fontSize="18px" color={Colors.Secondary.c2} fontFamily="PT Sans">
                This report is not currently available.
              </Text>
              <Text fontSize="16px" color={Colors.Secondary.c2} fontFamily="PT Sans">
                Please{" "}
                <Text as="span" color={Colors.Primary.accent} textDecoration="underline">
                  <a href="/contact-us">contact SNAP Collars</a>
                </Text>{" "}
                for assistance.
              </Text>
            </Flex>
          </ModalBody>
          <ModalFooter borderTop="1px solid" borderColor={Colors.Secondary.c2_300} justifyContent="flex-end" gap="8px">
            <Button
              bg="white"
              color={Colors.Primary.c1}
              border="2px solid"
              borderColor={Colors.Primary.c1}
              _hover={{ bg: Colors.Primary.c1, color: "white", borderColor: Colors.Primary.c1 }}
              onClick={onClose}
            >
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  )
}
